/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import FormControl from "@mui/material/FormControl";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { InputFieldProps } from "../../types/InputProps";
import { Box } from "../Box";
import { Chip } from "../Chip";
import { Close } from "@mui/icons-material";
import { InputAdornment } from "../InputAdornment";
import { InputLabel } from "../InputLabel";
import { MenuItem } from "../Menu";
import { Select } from "../Select";
import InputWrapper from "./InputWrapper";

const FormSelect = (props: InputFieldProps) => {
  const {
    name,
    control,
    label,
    info,
    hint,
    meta,
    selectKeys,
    options,
    validations = [],
  } = props;

  const memoizedOptions = useMemo(() => options, [options]);

  const showEndAdornment = (value: any) => {
    console.log(value);
    if (value) {
      if (value instanceof Array && value.length == 0) {
        return false;
      }
      return true;
    }
    return false;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <InputWrapper info={info} hint={hint} error={error}>
          <FormControl
            fullWidth
            required={validations.length > 0}
            error={!!error}
          >
            <InputLabel>{label}</InputLabel>
            <Select
              labelId={`${name}-select`}
              aria-label={`${name}-label ${value}`}
              required={validations.length > 0}
              label={label}
              sx={{
                minHeight: "55px",
                paddingTop: 1,
                paddingBottom: 1,
                alignItems: 'flex-start'
              }}
              onChange={(event) => onChange(event.target.value)}
              value={value}
              endAdornment={
                showEndAdornment(value) ? ( // Show clear button only if there's a selected value
                  <InputAdornment position="end" sx={{ alignSelf: 'center', mr:2 }}>
                    <Close
                      onClick={() => onChange(meta.multiple ? [] : "")}
                      sx={{ padding: 0.5, cursor: "pointer" }}
                      aria-label="Cancel Selection"
                    />
                  </InputAdornment>
                ) : null
              }
              {...(meta?.isChip
                ? {
                    renderValue: (selected: any) => {
                      if (typeof selected === "string") {
                        return (
                          <Chip title="Chip" key={selected} label={selected} />
                        );
                      }
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            maxHeight: 100,
                            overflowY: "auto",
                          }}
                          onMouseDown={(event) => event.stopPropagation()}
                        >
                          {selected?.map((fieldValue: any) => (
                            <Chip
                              key={fieldValue}
                              title={name}
                              label={fieldValue}
                              onDelete={() => {
                                onChange(
                                  value.filter(
                                    (chip: string) => chip !== fieldValue,
                                  ),
                                );
                              }}
                            />
                          ))}
                        </Box>
                      );
                    },
                  }
                : {})}
              {...meta}
            >
              {memoizedOptions &&
                memoizedOptions.map((option) => {
                  const key = selectKeys?.codeKey ?? "value";
                  const labelKey = selectKeys?.labelKey ?? "desc";
                  return (
                    <MenuItem
                      key={option[key as keyof typeof option]}
                      value={option.value}
                    >
                      {option[labelKey as keyof typeof option]}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </InputWrapper>
      )}
    />
  );
};

export default FormSelect;
